<template>
<v-card
    v-if="value != null"
>
<v-card-text>


    <LineChart 
        :chart-data="value.chart_data" 
        :chart-options="getChartOptions(value.layer)" 
        style="height: 350px"
    />

    <div
        class="edit-container"
    >
        <div 
            class="edit-ctrl-container"
        >
            <m-edit-text
                label="Weight"
                v-model="value.params.weight"
                number
                slider_min="0"
                slider_max="144"
                slider_step="1"
                :default_value="value.params_default.weight"
                edit_help_text="<p>Weight controls how much consideration the sentiment forecast model gives this layer.  Higher values give the layer more consideration, lower values less.</p><p>To disable this layer, set its weight to zero.</p>"
                v-on:input="onChange"
            />
        </div>
        <div
            class="edit-ctrl-container"
        >
            <m-edit-text
                label="Temperature"
                v-model="value.params.temperature"
                type="number"
                v-on:input="onChange"
                edit_help_text="<p>Temperature is a measure of anticipated day-to-day variation <i>in the same customer</i>.</p><p>A lower than default temperature indicates that you think your customers are more consistent, day-to-day, than average, while a higher than default temperature indicates that you think your customers are less consistant, day-to-day, than average.</p>"

                slider_min="0"
                slider_max="50"
                slider_step="0.5"
                :default_value="value.params_default.temperature"
            />
        </div>
        <div
            v-if="value.params.function.type == 'constant'"
            class="edit-ctrl-container"
        >
            <m-edit-text
                :label="'Advanced: \u03b2'"
                v-model="value.params.function.value"
                type="number"
                v-on:input="onChange"
                edit_help_text="<p>Sets the &beta; for the uncertanty background.  This changes the mean random value generated by the layer.</p>"

                slider_min="0"
                slider_max="100"
                slider_step="1"
                :default_value="value.params_default.value"
            />
        </div>

        <div
            v-if="value.params.function.type == 'cdf-normal'"
            class="edit-ctrl-container"
        >
            <m-edit-text
                :label="'Advanced: \u03C3'"
                v-model="value.params.function.variance"
                type="number"
                hint="Variance (squared scale)"
                v-on:input="onChange"

                slider_min="5"
                slider_max="50"
                slider_step="0.1"
                :default_value="value.params_default.function.variance"
                edit_help_text="<p>Sets the &sigma;<sup>2</sup> (customer variance) of the model.</p><p>Higher values of &sigma;<sup>2</sup> are used when your customers have higher variance (customer to customer) than average.  Lower values of &sigma;<sup>2</sup> are used when your customers have higher variance (customer to customer) than average.</p>"
            />
        </div>

        <div
            v-if="value.params.function.type == 'cdf-normal'"
            class="edit-ctrl-container"
        >
            <m-edit-text
                :label="'Advanced: \u03bc'"
                v-model="value.params.function.location"
                type="number"
                v-on:input="onChange"

                slider_min="0"
                slider_max="100"
                slider_step="1"
                :default_value="value.params_default.function.location"
                edit_help_text="<p>Sets the &mu; (mean or location) of the model.</p>"
            />
        </div>

        <div
            class="edit-btn-container"
        >
            <v-btn
                text
                v-on:click="onClickRestoreDefaults"
            >
                Restore Defaults...
            </v-btn>
        </div>
    </div>

    <div
        style="margin-bottom: 25px"
    >
        <b>Frequently Asked Questions (FAQ): {{getLayerProps(value.layer).title}}</b>
        <m-model-r1-faq
            :layer="value.layer"
        />
    </div> 

</v-card-text>
</v-card>
</template>

<script>
import { Line as LineChart } from 'vue-chartjs';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export default {
    components: {
        LineChart
    },

    props: {
        layer: {
            type: String,
            default: function() { return null; }
        },
    },

    data() {
        return {
            value: null,
        }
    },

    mounted () {
        this.doUpdate();
    },


    methods: {
        doUpdate()
        {
            if ( this.layer == null )
            {
                this.value = null;
                return;
            }

            let params = {};

            if ( this.value != null )
            {
                params = this.value.params;
            }
    

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/model-edit/r1/:organization_id/:bulk_message_id/:layer',

                params: {
                    'layer': this.layer, 
                    'organization_id': this.$route.params.organization_id,
                    'bulk_message_id': this.$route.params.bulk_message_id,
                },
            
                'on_success': (data) => {
                        this.setValue(data);
                    },
            
                'on_error': 'Unable to load layer '+this.layer,
            });
        },

        setValue(data)
        {
            this.value = data;

            this.value.chart_data.showLine = false;
            this.value.chart_data.datasets[0].borderColor = 'rgba(255,100,100,1.0)';
            this.value.chart_data.datasets[1].backgroundColor = 'rgba(0,0,0,0.1)';
        },
        
        onChange() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/model-edit/r1',
            
                'params' : {
                    'organization_id': this.$route.params.organization_id,
                    'bulk_message_id': this.$route.params.bulk_message_id,
                        ... this.value.params
                    },
            
                'on_success': (data) => {
                        this.setValue(data);
                    },
            
                'on_error': 'Unable to update model',
            });
        },

        onClickRestoreDefaults()
        {
            this.$rc.confirm({
                'title': 'Reset Defaults',
                'body': 'Reset layer to default settings?',
                'btn_text_ok': 'Reset',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.value.params = this.value.params_default;
                    this.doUpdate();
                },
            });
        },

        getLayerProps(layer)
        {
            if ( layer == 'background' )
            {
                return {
                    'title': 'Uncertainty Background',
                    'x_axis_title': 'Customer Hash (modulo 100)',
                };
            } 

            if ( layer == 'days-since-last-promotional-message' )
            {
                return {
                    'title': 'Time Since Last Promotional Message',
                    'x_axis_title': 'Days since last promotional message'
                };
            }

            if ( layer == 'days-since-last-non-club-purchase' )
            {
                return {
                    'title': 'Time Since Last Non Club Order Placed',
                    'x_axis_title': 'Days since last (non-club) order placed',
                };
            }

            if ( layer == 'bulk-message-order-attribution-percentile' )
            {
                return {
                    'title': 'Bulk Message Order Attribution Percentile',
                    'x_axis_title': 'Bulk message order attribution (percentile)',
                };
            }

            if ( layer == 'is-active-club-member' )
            {
                return {
                    'title': 'Is Active Club Member?',
                    'x_axis_title': 'Less than 50, not a club member, greater than 50, club member',
                };
            }

            return {
                'title': 'Unknown',
                'x_axis_title': 'Unknown',
            };
        },

        getChartOptions(layer)
        {
            let props = this.getLayerProps(layer);

            return {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        type: 'linear',
                        title: {
                            display: true,
                            text: props.x_axis_title,
                        },
                        ticks: {
                            stepSize: 1
                        },
                    },
                    y: {
                        type: 'linear',
                        title: {
                            display: true,
                            text: 'Sentiment Score (Percentile)'
                        },
                            ticks: {
                            stepSize: 1
                        }
                    },
                },
                plugins: {
                    tooltip: {
                        enabled: false
                    },
                    legend: {
                        display: false,
                    },
                    title: {
                        display: true, 
                        text: props.title,
                    }
                },
                animation: {
                    duration: 0
                }
            };
        }
    },
}
</script>

<style lang="scss" scoped>
.edit-container
{
    display: flex;
    gap: 15px;
}
.edit-ctrl-container
{
    flex-grow: 1;
}
.edit-btn-container
{
    flex-grow: 0;
    margin-top: 15px;
}
</style>